import { useEffect, useMemo, useRef } from "react";

import type {
  PlaygroundSessionResponse,
  SettingsResponse,
} from "@go-novus/admin-api";
import { mountWidget } from "@go-novus/widget";
import { useToast } from "@go-novus/ui";

import { createMerchantFeedback } from "#/features/feedback/feedback-api";
import { renewPlaygroundToken } from "#/features/playground";
import { useTheme } from "#/shared/lib/theme";

export interface PlaygroundWidgetProps {
  playgroundSession: PlaygroundSessionResponse;
  settings: SettingsResponse;
  onSessionReplaced?: () => void;
}

/**
 * 后台里的挂件。和店面是同一个 `mountWidget`，只是令牌和 `test:` 身份由
 * `POST /api/playground/sessions` 直接给，不经 App Proxy 握手。
 *
 * 换会话（Reset）或改配置都会重挂：挂件的会话状态在它自己里面，
 * 换了 uid 就该是一段新对话。
 *
 * 有效访客令牌过期不重挂；服务端已清理的旧身份恢复为新匿名测试时重挂。
 */
export function PlaygroundWidget({
  playgroundSession,
  settings,
  onSessionReplaced,
}: PlaygroundWidgetProps) {
  const replacement = useRef(onSessionReplaced);
  useEffect(() => {
    replacement.current = onSessionReplaced;
  }, [onSessionReplaced]);
  const host = useRef<HTMLDivElement>(null);
  const { theme } = useTheme();
  const { add: addToast } = useToast();
  const merchantFeedback = useMemo(
    () => ({
      ...createMerchantFeedback("playground"),
      onLikeError: (message: string) => {
        addToast({ data: { tone: "error" }, title: message });
      },
    }),
    [addToast],
  );

  useEffect(() => {
    const container = host.current;
    if (container === null) return;
    return mountWidget(container, {
      handshakeUrl: playgroundSession.handshakeUrl,
      ...(playgroundSession.voice_gateway_url === undefined
        ? {}
        : { voiceGatewayUrl: playgroundSession.voice_gateway_url }),
      playgroundSession: {
        renew: () =>
          renewPlaygroundToken(playgroundSession.uid, () =>
            replacement.current?.(),
          ),
        settings,
        capabilities: playgroundSession.capabilities,
        socketUrl: playgroundSession.socket_server_url,
        token: playgroundSession.token,
        uid: playgroundSession.uid,
      },
      merchantFeedback,
      shopDomain: playgroundSession.shopDomain,
    });
  }, [merchantFeedback, playgroundSession, settings]);

  return (
    <div className="min-h-0 flex-1" data-widget-surface={theme} ref={host} />
  );
}
